import type { LngLat } from "../../lib/geo/types.ts";

interface PhotonProperties {
  name?: string;
  street?: string;
  housenumber?: string;
  postcode?: string;
  city?: string;
  country?: string;
  osm_key?: string;
  osm_value?: string;
  osm_id?: number;
  osm_type?: string;
}

export type PhotonFeature = GeoJSON.Feature<GeoJSON.Point, PhotonProperties>;

// Photon exposes the primary OSM classification for each result through
// `osm_key` + `osm_value`. ADR-013 explains why this stays distinct from the
// app's finite POI category model.
export interface PhotonMainClassification {
  key: string;
  value: string;
}

const PHOTON_RESULT_LIMIT = 5;
const HIDDEN_PHOTON_CLASSIFICATIONS = new Set([
  // https://wiki.openstreetmap.org/wiki/Tag:place=region
  // "This is a tag from the earliest days of OpenStreetMap, is used in various ways, was never clearly defined and is rarely used by data consumers."
  "place=region",
]);

export async function fetchPhotonFeatures(
  photonApiUrl: string,
  query: string,
  center: LngLat,
): Promise<PhotonFeature[]> {
  const [lng, lat] = center;
  const params = new URLSearchParams({
    q: query,
    limit: String(PHOTON_RESULT_LIMIT),
    lon: String(lng),
    lat: String(lat),
  });
  // photonApiUrl already ends with "?" from config
  const url = photonApiUrl + params.toString();

  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(
      `Geocoding request failed: ${response.status} ${response.statusText}`,
    );
  }

  const data = await response.json() as GeoJSON.FeatureCollection;
  return filterPhotonFeatures(data.features as PhotonFeature[]);
}

export function filterPhotonFeatures(
  features: PhotonFeature[],
): PhotonFeature[] {
  return features.filter((feature) => {
    const classification = photonMainClassification(feature);
    return !HIDDEN_PHOTON_CLASSIFICATIONS.has(
      `${classification.key}=${classification.value}`,
    );
  });
}

export function photonMainClassification(
  feature: PhotonFeature,
): PhotonMainClassification {
  const key = nonEmptyString(feature.properties.osm_key, "osm_key");
  const value = nonEmptyString(feature.properties.osm_value, "osm_value");
  return { key, value };
}

function nonEmptyString(value: string | undefined, fieldName: string): string {
  const trimmed = value?.trim();
  if (trimmed) return trimmed;
  throw new Error(`Photon result is missing ${fieldName}.`);
}
